import { useState } from 'react';
import { Box, Typography, Stack, Avatar, Button, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CircleIcon from '@mui/icons-material/Circle';
import LockIcon from '@mui/icons-material/Lock';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Surface, PerformanceValue } from './primitives';
import { getInfoEdge, INFO_EDGE_TOOLTIP } from '../utils/infoEdge';

const EDGE_COLOR = {
    strong: 'success.main',
    likely: 'success.light',
    possible: 'warning.main',
    none: 'text.muted',
    insufficient: 'text.muted',
};

const TraderCard = ({ member, index, isTracked, onTrackingUpdate, isPremium }) => {
    const theme = useTheme();
    const [isLoading, setIsLoading] = useState(false);

    const {
        name,
        member_details: rawMemberDetails,
        avg_returns = {},
        bullish_transactions = {},
    } = member;
    // JSON often has member_details: null; destructuring default only applies for undefined.
    const member_details = rawMemberDetails ?? {};

    const purchaseROI = avg_returns.stocks?.purchase?.roi || 0;
    const purchaseCount = bullish_transactions.count || 0;
    const accuracy = bullish_transactions.accuracy || 0;
    const purchaseVolume = bullish_transactions.volume || 0;
    const profitEstimated = bullish_transactions.profit_estimated || 0;
    const alpha = bullish_transactions.alpha || null;
    const grade = member.grade || null;
    // P(true alpha > 0) under the empirical-Bayes posterior, 0-1.
    const alphaProb = member.alpha_prob_positive ?? null;

    const roiPercent = (purchaseROI - 1) * 100;
    const alphaPercent = alpha ? (alpha * 100) : null;
    // The 5% lower bound of the empirical-Bayes posterior, in percentage
    // points from the backend. This is a conservative FLOOR, not a corrected
    // estimate -- the posterior mean lives in member.alpha_adjusted and reads
    // higher. Named accordingly so the label matches the number: DelBene's
    // posterior mean is +17.4% but only +11.0% survives the bound.
    // Shown alongside the raw figure because the leaderboard sorts on it --
    // without it, a member with one +46% trade appears near the bottom of the
    // board while their card advertises +46%.
    const provenAlpha = member.alpha_lower_bound ?? null;

    const infoEdge = getInfoEdge(alphaProb, purchaseCount);
    const formattedVolume = new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD', maximumFractionDigits: 0,
    }).format(purchaseVolume);
    const formattedProfit = profitEstimated
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(profitEstimated)
        : '—';

    const memberImage = member_details?.image;
    const memberParty = member_details?.party || 'Unknown';
    const memberState = member_details?.state || '';
    const memberId = member_details?.bioguideId;

    const partyKey = memberParty === 'Democratic' ? 'democrat' : memberParty === 'Republican' ? 'republican' : 'independent';
    const partyColor = theme.palette.custom.party[partyKey];

    const handleTrackClick = async () => {
        if (!memberId) return;
        setIsLoading(true);
        try {
            const session = await fetchAuthSession();
            const idToken = session.tokens.idToken.toString();
            const response = await fetch('https://api.capitolgains.io/user/track-member', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
                body: JSON.stringify({ members: [memberId] }),
            });
            if (!response.ok) throw new Error('Failed to update tracking');
            await response.json();
            onTrackingUpdate(memberId, isTracked);
        } catch (error) {
            console.error('Error updating tracking:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: Math.min(index, 10) * 0.03 }}
        >
            <Surface
                padding={0}
                sx={{
                    overflow: 'hidden',
                    transition: t => `border-color ${t.customTransitions.base}`,
                    '&:hover': { borderColor: 'custom.border.default' },
                }}
            >
                {/* Header */}
                <Box sx={{
                    p: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    borderBottom: t => `1px solid ${t.palette.custom.border.subtle}`,
                }}>
                    <Typography
                        sx={{
                            fontFamily: t => t.typography.fontFamilyMono,
                            color: 'text.muted',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            width: 32,
                            textAlign: 'right',
                            flexShrink: 0,
                        }}
                    >
                        {index + 1}
                    </Typography>
                    <Avatar
                        src={memberImage}
                        alt={name}
                        sx={{
                            width: 44, height: 44,
                            border: `2px solid ${partyColor}40`,
                            flexShrink: 0,
                        }}
                    />
                    <Box minWidth={0} flex={1}>
                        <Typography
                            component={Link}
                            to={`/congressmember/${memberId || ''}`}
                            sx={{
                                display: 'block',
                                color: 'text.primary',
                                fontWeight: 600,
                                fontSize: '0.9375rem',
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                '&:hover': { color: 'primary.light' },
                            }}
                        >
                            {name}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <CircleIcon sx={{ fontSize: 7, color: partyColor }} />
                            <Typography variant="caption" sx={{ color: 'text.muted' }}>
                                {memberParty} · {memberState}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Stats rows */}
                <Stack divider={<Box sx={{ borderTop: t => `1px solid ${t.palette.custom.border.subtle}` }} />}>
                    <StatRow label="Information Edge">
                        <Tooltip
                            title={INFO_EDGE_TOOLTIP}
                            placement="left"
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {grade && (
                                    <Typography sx={{
                                        ...monoValueSx,
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        color: grade.startsWith('A') ? 'success.main'
                                            : grade.startsWith('B') ? 'primary.light'
                                            : grade.startsWith('C') ? 'text.secondary'
                                            : 'error.main',
                                    }}>
                                        {grade}
                                    </Typography>
                                )}
                                <Typography sx={{
                                    ...monoValueSx,
                                    fontWeight: 600,
                                    color: EDGE_COLOR[infoEdge.key],
                                }}>
                                    {infoEdge.label}
                                </Typography>
                            </Box>
                        </Tooltip>
                    </StatRow>
                    {alphaPercent != null && (
                        <StatRow label="Alpha vs S&P 500">
                            <PerformanceValue value={alphaPercent} variant="body2" />
                        </StatRow>
                    )}
                    {provenAlpha != null && (
                        <Tooltip
                            title="The alpha that holds up statistically — a conservative floor rather than a best guess. A record built on few trades is shrunk toward the congressional average and further discounted for uncertainty; a long record is barely adjusted. This is what the leaderboard ranks on."
                            arrow
                        >
                            <Box>
                                <StatRow label="Proven Alpha">
                                    <PerformanceValue value={provenAlpha} variant="body2" />
                                </StatRow>
                            </Box>
                        </Tooltip>
                    )}
                    <StatRow label="Annual Return">
                        <PerformanceValue value={roiPercent} variant="body2" />
                    </StatRow>
                    <StatRow label="Hit Rate">
                        <Typography sx={monoValueSx}>{(accuracy * 100).toFixed(1)}%</Typography>
                    </StatRow>
                    <StatRow label="Purchases">
                        <Typography sx={monoValueSx}>{purchaseCount}</Typography>
                    </StatRow>
                    <StatRow label="Avg. Position">
                        <Typography sx={monoValueSx}>{formattedVolume}</Typography>
                    </StatRow>
                    <StatRow label="Est. Profit">
                        <Typography sx={monoValueSx}>{formattedProfit}</Typography>
                    </StatRow>
                </Stack>

                {/* Track CTA */}
                <Box sx={{ p: 2, borderTop: t => `1px solid ${t.palette.custom.border.subtle}` }}>
                    {isPremium ? (
                        <Tooltip
                            title={isTracked
                                ? 'Remove from email-alert watchlist'
                                : 'Add to watchlist for email alerts on new trades'}
                            placement="top"
                        >
                            <Button
                                variant={isTracked ? 'outlined' : 'contained'}
                                size="small"
                                fullWidth
                                onClick={handleTrackClick}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Updating…' : isTracked ? 'Remove from Watchlist' : 'Add to Watchlist'}
                            </Button>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Upgrade to Pro to track members" placement="top">
                            <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                component={Link}
                                to="/pricing"
                                startIcon={<LockIcon sx={{ fontSize: 14 }} />}
                            >
                                Pro Feature
                            </Button>
                        </Tooltip>
                    )}
                </Box>
            </Surface>
        </motion.div>
    );
};

// ── Internal helpers ──────────────────────────────────────────────────────

const monoValueSx = {
    fontFamily: t => t.typography.fontFamilyMono,
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'text.primary',
};

function StatRow({ label, children }) {
    return (
        <Box
            sx={{
                px: 2.5,
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                {label}
            </Typography>
            {children}
        </Box>
    );
}

export default TraderCard;
